import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import OrdersSubSidebar from '../components/OrdersSubSidebar';
import LanguageToggle from '../components/LanguageToggle';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';
import { apiClient } from '../lib/apiClient';
import { LogOut, CheckCircle2, XCircle, ClipboardList, Loader2, RotateCcw } from 'lucide-react';

const STATUS_STYLE = {
  submitted: { bg: 'bg-amber-100', text: 'text-amber-700', label: { tr: 'Onay Bekliyor', en: 'Pending' } },
  approved:  { bg: 'bg-emerald-100', text: 'text-emerald-700', label: { tr: 'Onaylandı', en: 'Approved' } },
  rejected:  { bg: 'bg-red-100', text: 'text-red-700', label: { tr: 'Reddedildi', en: 'Rejected' } },
  paid:      { bg: 'bg-blue-100', text: 'text-blue-700', label: { tr: 'Ödendi', en: 'Paid' } },
  delivered: { bg: 'bg-violet-100', text: 'text-violet-700', label: { tr: 'Teslim', en: 'Delivered' } },
  refunded:  { bg: 'bg-orange-100', text: 'text-orange-700', label: { tr: 'İade Edildi', en: 'Refunded' } },
};

export default function UltraAdminServiceOrdersPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const tr = language === 'tr';
  const t = (en, trTxt) => (tr ? trTxt : en);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [refundFor, setRefundFor] = useState(null);
  const [refundNote, setRefundNote] = useState('');
  const [refundUltraPwd, setRefundUltraPwd] = useState('');
  const [refundConfirm, setRefundConfirm] = useState(false);
  const [refunding, setRefunding] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/ultra-admin/service-orders${statusFilter ? `?status=${statusFilter}` : ''}`);
      setOrders(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { fetch(); }, [statusFilter]);

  const approve = async (id) => {
    if (!window.confirm(t('Approve this order?','Bu siparişi onayla?'))) return;
    try { await apiClient.post(`/ultra-admin/service-orders/${id}/approve`, { note: '' }); await fetch(); setDetail(null); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const reject = async () => {
    if (!rejectNote.trim()) return;
    try {
      await apiClient.post(`/ultra-admin/service-orders/${rejectFor}/reject`, { note: rejectNote });
      setRejectFor(null); setRejectNote(''); setDetail(null);
      await fetch();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const refund = async () => {
    if (!refundConfirm) return;
    if ((refundNote || '').trim().length < 5) { alert(t('Note must be at least 5 characters','Not en az 5 karakter olmalı')); return; }
    if (!refundUltraPwd) { alert(t('Ultra Password required','Ultra Password gerekli')); return; }
    setRefunding(true);
    try {
      await apiClient.post(`/ultra-admin/service-orders/${refundFor}/refund`, {
        note: refundNote, ultra_password: refundUltraPwd,
      });
      setRefundFor(null); setRefundNote(''); setRefundUltraPwd(''); setRefundConfirm(false);
      setDetail(null);
      await fetch();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
    setRefunding(false);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />
      <OrdersSubSidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2"><ClipboardList className="w-5 h-5" /> {t('Service Orders','Servis Siparişleri')}</h1>
            <p className="text-xs text-gray-500 mt-0.5">{t('Review and manage exhibitor service orders','Katılımcı servis siparişlerini inceleyin ve yönetin')}</p>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <button onClick={() => { localStorage.clear(); navigate('/giris'); }} className="text-gray-400 hover:text-red-500">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 flex-1">
          <PageIntro {...getPageIntro('service-orders', language)} />
          {/* Filter */}
          <div className="flex gap-2 mb-4">
            {['', 'submitted', 'approved', 'rejected', 'paid', 'refunded'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)} data-testid={`filter-${s || 'all'}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  statusFilter === s ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}>
                {s ? (STATUS_STYLE[s]?.label[tr ? 'tr' : 'en'] || s) : t('All','Tümü')}
              </button>
            ))}
          </div>

          {loading && <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-5 h-5 animate-spin mr-2" /> {t('Loading','Yükleniyor...')}</div>}

          {!loading && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-4 py-2 text-left">{t('Tenant','Tenant')}</th>
                    <th className="px-4 py-2 text-left">{t('Stand','Stand')}</th>
                    <th className="px-4 py-2 text-left">{t('Items','Ürün')}</th>
                    <th className="px-4 py-2 text-right">{t('Total','Toplam')}</th>
                    <th className="px-4 py-2 text-center">{t('Status','Durum')}</th>
                    <th className="px-4 py-2 text-center">{t('Actions','Aksiyon')}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => {
                    const st = STATUS_STYLE[o.status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: { tr: o.status, en: o.status } };
                    return (
                      <tr key={o.id} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-2 font-semibold text-gray-800">{o.tenant_name}</td>
                        <td className="px-4 py-2 text-gray-700">{o.stand_no} <span className="text-xs text-gray-400">· {o.hall_name}</span></td>
                        <td className="px-4 py-2 text-gray-600">{(o.items || []).length} {t('items','ürün')}</td>
                        <td className="px-4 py-2 text-right font-bold">{o.total} {o.currency}</td>
                        <td className="px-4 py-2 text-center"><span className={`text-xs px-2 py-0.5 rounded-full ${st.bg} ${st.text}`}>{st.label[tr ? 'tr' : 'en']}</span></td>
                        <td className="px-4 py-2 text-center">
                          <button onClick={() => setDetail(o)} data-testid={`detail-${o.id}`} className="text-purple-600 hover:underline text-xs">{t('View','İncele')}</button>
                        </td>
                      </tr>
                    );
                  })}
                  {orders.length === 0 && <tr><td colSpan="6" className="text-center text-gray-400 py-8">{t('No orders.','Sipariş yok.')}</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* DETAIL */}
      {detail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-800">{t('Order','Sipariş')} #{detail.id.slice(0, 8)}</h2>
              <button onClick={() => setDetail(null)} className="text-gray-400">✕</button>
            </div>
            <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label={t('Tenant','Tenant')} value={detail.tenant_name} />
                <Field label={t('User','Kullanıcı')} value={detail.user_email} />
                <Field label={t('Stand','Stand')} value={`${detail.stand_no} · ${detail.hall_name}`} />
                <Field label={t('Zone','Zone')} value={detail.zone} />
                <Field label={t('Created','Oluşturma')} value={new Date(detail.created_at).toLocaleString()} />
                <Field label={t('Status','Durum')} value={detail.status} />
              </div>
              {detail.customer_notes && <Field label={t('Customer notes','Müşteri notu')} value={detail.customer_notes} />}
              {detail.admin_note && <Field label={t('Admin note','Admin notu')} value={detail.admin_note} />}
              {detail.refund_note && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-xs">
                  <div className="font-semibold text-orange-800 mb-1">🔄 {t('Refund recorded','İade kaydı')}</div>
                  <div className="text-orange-700">{detail.refund_note}</div>
                  <div className="text-orange-500 mt-1">{detail.refunded_by_admin_email} · {detail.refunded_at && new Date(detail.refunded_at).toLocaleString()}</div>
                </div>
              )}
              <div>
                <h3 className="text-sm font-bold text-gray-700 mb-2">{t('Items','Ürünler')}</h3>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 text-gray-600">
                      <tr><th className="px-3 py-2 text-left">{t('Product','Ürün')}</th><th className="px-3 py-2 text-right">{t('Qty','Adet')}</th><th className="px-3 py-2 text-right">{t('Unit','Birim')}</th><th className="px-3 py-2 text-right">{t('Subtotal','Ara Toplam')}</th></tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((it, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-3 py-2">
                            {it.title}
                            {it.notes && <div className="text-gray-400 italic">"{it.notes}"</div>}
                          </td>
                          <td className="px-3 py-2 text-right">{it.quantity}</td>
                          <td className="px-3 py-2 text-right">{it.unit_price} {it.currency}</td>
                          <td className="px-3 py-2 text-right font-semibold">{it.subtotal} {it.currency}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr><td colSpan="3" className="px-3 py-2 text-right font-bold">{t('Total','Toplam')}</td><td className="px-3 py-2 text-right font-bold">{detail.total} {detail.currency}</td></tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              {detail.status === 'submitted' && (
                <div className="flex gap-2 pt-3 border-t border-gray-100">
                  <button onClick={() => { setRejectFor(detail.id); }} data-testid="reject-btn"
                    className="flex-1 py-2.5 rounded-xl border border-red-300 text-red-700 text-sm font-semibold hover:bg-red-50 flex items-center justify-center gap-2">
                    <XCircle className="w-4 h-4" /> {t('Reject','Reddet')}
                  </button>
                  <button onClick={() => approve(detail.id)} data-testid="approve-btn"
                    className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold flex items-center justify-center gap-2"
                    style={{ background: 'linear-gradient(135deg, #059669, #10b981)' }}>
                    <CheckCircle2 className="w-4 h-4" /> {t('Approve','Onayla')}
                  </button>
                </div>
              )}
              {(detail.status === 'approved' || detail.status === 'paid') && (
                <div className="pt-3 border-t border-gray-100">
                  <button onClick={() => { setRefundFor(detail.id); setRefundNote(''); setRefundUltraPwd(''); setRefundConfirm(false); }}
                    data-testid="refund-btn"
                    className="w-full py-2.5 rounded-xl border border-orange-300 text-orange-700 text-sm font-semibold hover:bg-orange-50 flex items-center justify-center gap-2">
                    <RotateCcw className="w-4 h-4" /> {t('Refund / Mark as Refunded','İade Et / İade İşaretle')}
                  </button>
                  <p className="text-[10px] text-gray-400 mt-2 text-center">
                    {t('Manual refund — Stripe automation deferred. Marks status=refunded so stand can be unassigned.',
                       'Manuel iade — Stripe otomasyonu sonraki phase\'de. Status=refunded işaretlenir, stand kaldırılabilir hale gelir.')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {rejectFor && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="font-bold text-gray-800 mb-3">{t('Reject order — reason required','Reddetme — gerekçe zorunlu')}</h3>
            <textarea rows="3" value={rejectNote} onChange={e => setRejectNote(e.target.value)}
              data-testid="reject-note"
              placeholder={t('Reason...','Sebep...')}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
            <div className="flex gap-2 mt-3">
              <button onClick={() => { setRejectFor(null); setRejectNote(''); }} className="flex-1 py-2 rounded-xl border border-gray-200 text-sm">{t('Cancel','İptal')}</button>
              <button onClick={reject} disabled={!rejectNote.trim()} data-testid="confirm-reject" className="flex-1 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold disabled:opacity-50">{t('Reject','Reddet')}</button>
            </div>
          </div>
        </div>
      )}

      {/* REFUND MODAL — high-risk action, requires Ultra Password + explicit checkbox */}
      {refundFor && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-2 mb-3">
              <RotateCcw className="w-5 h-5 text-orange-600" />
              <h3 className="font-bold text-gray-800">{t('Refund Order','Sipariş İadesi')}</h3>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 text-xs text-orange-800 mb-3">
              <strong>⚠ {t('Warning','Uyarı')}:</strong> {t('This marks the order as REFUNDED in our system. Stripe refund automation is deferred to a later phase — execute the actual refund manually first, then mark it here.','Bu işlem siparişi sistemimizde İADE EDİLDİ olarak işaretler. Stripe otomasyonu sonraki phase\'de — gerçek iadeyi önce manuel olarak yapın, sonra burada işaretleyin.')}
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{t('Refund reason / note (min 5 chars)','İade gerekçesi (en az 5 karakter)')} *</label>
                <textarea rows="3" value={refundNote} onChange={e => setRefundNote(e.target.value)}
                  data-testid="refund-note"
                  placeholder={t('e.g. Customer cancelled — Stripe refund #abc123','örn. Müşteri iptal etti — Stripe refund #abc123')}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">{t('Ultra Password','Ultra Şifre')} *</label>
                <input type="password" value={refundUltraPwd} onChange={e => setRefundUltraPwd(e.target.value)}
                  data-testid="refund-ultra-password"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono" />
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-700">
                <input type="checkbox" checked={refundConfirm} onChange={e => setRefundConfirm(e.target.checked)} data-testid="refund-confirm" className="mt-0.5" />
                <span>{t('I confirm the actual refund has been processed (or will be processed manually) and acknowledge this action cannot be reversed.','Gerçek iadenin yapıldığını (veya manuel olarak yapılacağını) onaylıyorum ve bu işlemin geri alınamayacağını biliyorum.')}</span>
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => { setRefundFor(null); setRefundNote(''); setRefundUltraPwd(''); setRefundConfirm(false); }} className="flex-1 py-2 rounded-xl border border-gray-200 text-sm">{t('Cancel','İptal')}</button>
              <button onClick={refund}
                disabled={refunding || !refundConfirm || (refundNote || '').trim().length < 5 || !refundUltraPwd}
                data-testid="confirm-refund"
                className="flex-1 py-2 rounded-xl bg-orange-600 text-white text-sm font-semibold disabled:opacity-50">
                {refunding ? '...' : t('Confirm Refund','İadeyi Onayla')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-semibold text-gray-800 text-sm break-words">{value ?? '—'}</div>
    </div>
  );
}
